import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
  ChevronLeft, ChevronRight, Share2, ExternalLink, Copy, Mail, MessageCircle,
  Package, Play, Tag, Globe, ShoppingBag, Eye, ArrowRight, Phone, Linkedin, Facebook,
  Send, Bookmark, Instagram, PhoneCall, ExternalLink as LinkIcon, Video, X, Headphones, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  product: { label: 'Product', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  service: { label: 'Service', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  bundle: { label: 'Bundle', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  subscription: { label: 'Subscription', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  other: { label: 'Other', color: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
};

function formatCurrency(val, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(val || 0);
}

function formatUnitLabel(unit) {
  if (!unit || unit === 'each') return '';
  const map = { project: 'per project', hour: 'per hour', month: 'per month', year: 'per year' };
  return map[unit] || `per ${unit}`;
}

function detectPlatform(referrer, params) {
  const src = (params.get('utm_source') || '').toLowerCase();
  const ref = (referrer || '').toLowerCase();
  if (src.includes('linkedin') || ref.includes('linkedin')) return 'linkedin';
  if (src.includes('facebook') || ref.includes('facebook') || ref.includes('fb.com')) return 'facebook';
  if (src.includes('twitter') || ref.includes('twitter') || ref.includes('t.co')) return 'twitter';
  if (src.includes('whatsapp') || ref.includes('whatsapp')) return 'whatsapp';
  if (src.includes('email') || src.includes('mail')) return 'email';
  if (src.includes('messenger') || ref.includes('messenger')) return 'messenger';
  return 'direct';
}

function getVideoEmbedUrl(url) {
  if (!url) return '';
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=0&rel=0&modestbranding=1`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
}

// ─── Media Carousel ───
function ShowcaseCarousel({ images = [], videoUrl = '' }) {
  const [idx, setIdx] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const allMedia = [
    ...images.map(url => ({ type: 'image', url })),
    ...(videoUrl ? [{ type: 'video', url: videoUrl }] : []),
  ];
  const total = allMedia.length;

  const goNext = () => setIdx(i => (i + 1) % total);
  const goPrev = () => setIdx(i => (i - 1 + total) % total);

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) { diff > 0 ? goNext() : goPrev(); }
  };

  if (total === 0) {
    return (
      <div className="w-full aspect-video flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl" data-testid="showcase-no-media">
        <Package className="h-16 w-16 sm:h-24 sm:w-24 text-slate-700" />
      </div>
    );
  }

  const current = allMedia[idx];

  return (
    <>
      {/* Main Carousel */}
      <div
        className="relative w-full aspect-video overflow-hidden rounded-none sm:rounded-2xl group cursor-pointer"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={() => setFullscreen(true)}
        data-testid="showcase-carousel"
      >
        {/* Deep background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#040d1a] via-[#081b33] to-[#040d1a]" />

        {/* Media — full bleed, no padding on mobile */}
        <div className="absolute inset-0 flex items-center justify-center sm:p-6">
          <div className="relative w-full h-full">
            {current.type === 'image' ? (
              <img
                src={current.url}
                alt=""
                className="w-full h-full object-contain select-none sm:rounded-xl"
                draggable={false}
                data-testid="showcase-media-image"
              />
            ) : (
              <div className="w-full h-full sm:rounded-xl overflow-hidden">
                <iframe
                  src={getVideoEmbedUrl(current.url)}
                  className="w-full h-full border-0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Product Video"
                  data-testid="showcase-media-video"
                />
              </div>
            )}
          </div>
        </div>

        {/* Tap hint on mobile */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 sm:hidden z-10 bg-black/50 backdrop-blur-md rounded-full px-3 py-1 text-[10px] text-white/60 flex items-center gap-1 pointer-events-none opacity-0 group-active:opacity-100 transition-opacity">
          <Eye className="h-3 w-3" /> Tap for full view
        </div>

        {/* Navigation arrows */}
        {total > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/80 hover:bg-white/20 transition-all z-10 active:scale-95"
              data-testid="showcase-carousel-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/80 hover:bg-white/20 transition-all z-10 active:scale-95"
              data-testid="showcase-carousel-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Dots */}
        {total > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {allMedia.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  i === idx ? "w-5 bg-emerald-400" : "w-1.5 bg-white/30 hover:bg-white/50"
                )}
              />
            ))}
          </div>
        )}

        {/* Video badge */}
        {current.type === 'video' && (
          <div className="absolute top-3 left-3 z-10">
            <Badge className="bg-red-500/80 text-white text-[10px] gap-1 backdrop-blur-md border-0">
              <Play className="h-3 w-3" />Video
            </Badge>
          </div>
        )}

        {/* Counter */}
        {total > 1 && (
          <div className="absolute top-3 right-3 z-10 bg-black/40 backdrop-blur-md rounded-md px-2 py-0.5 text-[10px] text-white/70">
            {idx + 1} / {total}
          </div>
        )}
      </div>

      {/* Fullscreen Immersive Overlay */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[60] bg-black flex flex-col"
          data-testid="showcase-fullscreen"
        >
          {/* Close button */}
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-3 right-3 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/20 transition-all"
            data-testid="fullscreen-close"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Counter */}
          {total > 1 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 text-xs text-white/60">
              {idx + 1} / {total}
            </div>
          )}

          {/* Full-bleed media */}
          <div
            className="flex-1 flex items-center justify-center"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {current.type === 'image' ? (
              <img
                src={current.url}
                alt=""
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
              />
            ) : (
              <div className="w-full h-full max-w-[100vw] max-h-[100vh] aspect-video mx-auto">
                <iframe
                  src={getVideoEmbedUrl(current.url)}
                  className="w-full h-full border-0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Product Video"
                />
              </div>
            )}
          </div>

          {/* Fullscreen nav arrows */}
          {total > 1 && (
            <>
              <button
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/80 hover:bg-white/20 z-20"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/80 hover:bg-white/20 z-20"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Fullscreen dots */}
          {total > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              {allMedia.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={cn(
                    "h-2 rounded-full transition-all duration-300",
                    i === idx ? "w-6 bg-emerald-400" : "w-2 bg-white/30"
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}



// ─── Contact Drawer (Mobile-optimized) ───
function ContactDrawer({ open, onClose, product, companyName, companyEmail, companyPhone, channels, voiceSlug }) {
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({ name: '', contact: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const productName = product?.name || '';
  const ch = channels || {};

  const contactOptions = [
    companyPhone && { id: 'call', label: 'Call', icon: PhoneCall, color: 'bg-emerald-600', url: `tel:${companyPhone}` },
    (ch.whatsapp || companyPhone) && { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'bg-[#25D366]', url: `https://wa.me/${(ch.whatsapp || companyPhone).replace(/[^0-9+]/g, '')}?text=${encodeURIComponent(`Hi, I am interested in ${productName}. Can you share more details?`)}` },
    ch.messenger && { id: 'messenger', label: 'Messenger', icon: MessageCircle, color: 'bg-[#006AFF]', url: ch.messenger.startsWith('http') ? ch.messenger : `https://m.me/${ch.messenger}` },
    ch.telegram && { id: 'telegram', label: 'Telegram', icon: Send, color: 'bg-[#0088cc]', url: ch.telegram.startsWith('http') ? ch.telegram : `https://t.me/${ch.telegram.replace('@', '')}` },
    companyEmail && { id: 'email', label: 'Email', icon: Mail, color: 'bg-slate-600', url: `mailto:${companyEmail}?subject=Inquiry about ${encodeURIComponent(productName)}&body=${encodeURIComponent(`Hi,\n\nI am interested in ${productName}. Could you please share more details?\n\nThank you.`)}` },
    ch.instagram && { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737]', url: ch.instagram.startsWith('http') ? ch.instagram : `https://instagram.com/${ch.instagram.replace('@', '')}` },
    ch.facebook && { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'bg-[#1877F2]', url: ch.facebook.startsWith('http') ? ch.facebook : `https://facebook.com/${ch.facebook}` },
    ch.linkedin && { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'bg-[#0A66C2]', url: ch.linkedin.startsWith('http') ? ch.linkedin : `https://linkedin.com/company/${ch.linkedin}` },
    ch.tiktok && { id: 'tiktok', label: 'TikTok', icon: Video, color: 'bg-black', url: ch.tiktok.startsWith('http') ? ch.tiktok : `https://tiktok.com/@${ch.tiktok.replace('@', '')}` },
    ch.website && { id: 'website', label: 'Website', icon: Globe, color: 'bg-slate-500', url: ch.website.startsWith('http') ? ch.website : `https://${ch.website}` },
    voiceSlug && { id: 'voice', label: 'AI Receptionist', icon: Headphones, color: 'bg-violet-600', url: `/voice/${voiceSlug}` },
  ].filter(Boolean);

  if (contactOptions.length === 0 && companyEmail) {
    contactOptions.push({ id: 'email', label: 'Email', icon: Mail, color: 'bg-slate-600', url: `mailto:${companyEmail}?subject=Inquiry about ${encodeURIComponent(productName)}` });
  }

  const handleTrack = (channelId) => {
    const token = product?.share_token;
    if (token) {
      fetch(`${API}/api/sales/products/public/${token}/track?platform=${channelId}&event=contact`, { method: 'POST' }).catch(() => {});
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryForm.name.trim() || !inquiryForm.contact.trim()) return;
    setSubmitting(true);
    try {
      const token = product?.share_token;
      const res = await fetch(`${API}/api/sales/products/public/${token}/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inquiryForm),
      });
      if (res.ok) {
        setSubmitted(true);
        setInquiryForm({ name: '', contact: '', message: '' });
      }
    } catch {}
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="contact-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700/50 rounded-t-2xl p-5 pb-8 animate-slideUp max-h-[85vh] overflow-y-auto">
        <div className="w-10 h-1 bg-slate-700 rounded-full mx-auto mb-4" />

        {!showInquiry ? (
          <>
            <div className="mb-4">
              <h3 className="text-white font-semibold text-base">Contact {companyName || 'Us'}</h3>
              <p className="text-xs text-slate-400 mt-0.5">Choose your preferred way to connect</p>
            </div>
            {contactOptions.length > 0 ? (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                {contactOptions.map(opt => (
                  <a
                    key={opt.id}
                    href={opt.url}
                    target={opt.id === 'call' ? '_self' : '_blank'}
                    rel="noopener noreferrer"
                    onClick={() => handleTrack(opt.id)}
                    className="flex flex-col items-center gap-1.5"
                    data-testid={`contact-${opt.id}`}
                  >
                    <div className={cn("w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg transition-transform active:scale-90", opt.color)}>
                      <opt.icon className="h-5 w-5" />
                    </div>
                    <span className="text-[10px] text-slate-400 text-center leading-tight">{opt.label}</span>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No contact information available</p>
            )}

            {/* Quick Inquiry button */}
            <div className="mt-5 pt-4 border-t border-slate-800">
              <button
                onClick={() => setShowInquiry(true)}
                className="w-full py-3 rounded-xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-sm font-medium hover:bg-emerald-600/30 transition-colors flex items-center justify-center gap-2"
                data-testid="quick-inquiry-btn"
              >
                <Mail className="h-4 w-4" />
                Send a Quick Inquiry
              </button>
            </div>
          </>
        ) : submitted ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
              <Mail className="h-6 w-6 text-emerald-400" />
            </div>
            <h3 className="text-white font-semibold text-base mb-1">Inquiry Sent!</h3>
            <p className="text-sm text-slate-400">We&apos;ll get back to you shortly.</p>
            <button onClick={onClose} className="mt-4 text-xs text-emerald-400 hover:text-emerald-300">Close</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <button onClick={() => setShowInquiry(false)} className="text-slate-400 hover:text-white">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h3 className="text-white font-semibold text-base">Quick Inquiry</h3>
            </div>
            <form onSubmit={handleInquirySubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Your Name *"
                value={inquiryForm.name}
                onChange={e => setInquiryForm(f => ({ ...f, name: e.target.value }))}
                required
                className="w-full px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50"
                data-testid="inquiry-name"
              />
              <input
                type="text"
                placeholder="Email or Phone *"
                value={inquiryForm.contact}
                onChange={e => setInquiryForm(f => ({ ...f, contact: e.target.value }))}
                required
                className="w-full px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50"
                data-testid="inquiry-contact"
              />
              <textarea
                placeholder="Your message (optional)"
                value={inquiryForm.message}
                onChange={e => setInquiryForm(f => ({ ...f, message: e.target.value }))}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50 resize-none"
                data-testid="inquiry-message"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="inquiry-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {submitting ? 'Sending...' : 'Send Inquiry'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}


// ─── Share Drawer (Mobile-optimized) ───
function ShareDrawer({ open, onClose, url, productName, price, imageUrl, shareToken }) {
  if (!open) return null;

  // OG URL for social crawlers — cache-buster forces re-crawl after code changes
  const ogUrl = shareToken ? `${window.location.origin}/api/sales/products/og/${shareToken}?v=3` : url;
  const shareText = `${productName} — ${price}`;
  const encodedOgUrl = encodeURIComponent(ogUrl);
  // WhatsApp: URL on its own line triggers rich link preview
  const waText = encodeURIComponent(`${shareText}\n${ogUrl}`);

  const platforms = [
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'bg-[#0A66C2]', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedOgUrl}` },
    { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'bg-[#1877F2]', url: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}` },
    { id: 'twitter', label: 'X', icon: Globe, color: 'bg-black', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodedOgUrl}` },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'bg-[#25D366]', url: `https://api.whatsapp.com/send?text=${waText}` },
    { id: 'messenger', label: 'Messenger', icon: MessageCircle, color: 'bg-[#006AFF]', url: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}&t=${encodeURIComponent(shareText)}` },
    { id: 'instagram', label: 'Instagram', icon: Globe, color: 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]', action: 'copy' },
    { id: 'tiktok', label: 'TikTok', icon: Globe, color: 'bg-black', action: 'copy' },
    { id: 'reddit', label: 'Reddit', icon: Globe, color: 'bg-[#FF4500]', url: `https://www.reddit.com/submit?url=${encodedOgUrl}&title=${encodeURIComponent(shareText)}` },
    { id: 'email', label: 'Email', icon: Mail, color: 'bg-slate-600', url: `mailto:?subject=${encodeURIComponent(productName)}&body=${encodeURIComponent(shareText + '\n\n' + ogUrl)}` },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(ogUrl);
    if (shareToken) {
      fetch(`${API}/api/sales/products/public/${shareToken}/track?platform=copy&event=share`, { method: 'POST' }).catch(() => {});
    }
    onClose();
  };

  const handlePlatformClick = (p) => {
    if (shareToken) {
      fetch(`${API}/api/sales/products/public/${shareToken}/track?platform=${p.id}&event=share`, { method: 'POST' }).catch(() => {});
    }
    // Instagram/TikTok: copy link
    if (p.action === 'copy') {
      navigator.clipboard.writeText(ogUrl);
      // Show brief toast if available
      return;
    }
    // Messenger: try deep link
    if (p.fallback) {
      const w = window.open(p.url, '_blank');
      setTimeout(() => { if (!w || w.closed) window.open(p.fallback, '_blank'); }, 500);
      return;
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="share-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700/50 rounded-t-2xl p-5 pb-8 animate-slideUp">
        <div className="w-10 h-1 bg-slate-700 rounded-full mx-auto mb-4" />
        <h3 className="text-white font-semibold text-sm mb-3">Share this product</h3>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {platforms.map(p => (
            <a
              key={p.id}
              href={p.action ? '#' : p.url}
              target={p.action ? undefined : '_blank'}
              rel="noopener noreferrer"
              onClick={(e) => { if (p.action || p.fallback) e.preventDefault(); handlePlatformClick(p); }}
              className={cn("flex flex-col items-center gap-1.5 min-w-[56px]")}
              data-testid={`public-share-${p.id}`}
            >
              <div className={cn("w-12 h-12 rounded-full flex items-center justify-center text-white", p.color)}>
                <p.icon className="h-5 w-5" />
              </div>
              <span className="text-[10px] text-slate-400">{p.label}</span>
            </a>
          ))}
          <button onClick={handleCopy} className="flex flex-col items-center gap-1.5 min-w-[56px]" data-testid="public-share-copy">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white bg-slate-700">
              <Copy className="h-5 w-5" />
            </div>
            <span className="text-[10px] text-slate-400">Copy</span>
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Main Public Showcase Page ───
export default function PublicShowcasePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [platform, setPlatform] = useState('direct');
  const pageRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    setPlatform(detectPlatform(document.referrer, searchParams));

    fetch(`${API}/api/sales/products/public/${token}`)
      .then(res => {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then(data => {
        setProduct(data);
        // Track view with detected platform
        const src = detectPlatform(document.referrer, searchParams);
        fetch(`${API}/api/sales/products/public/${token}/track?platform=${src}&event=view`, { method: 'POST' }).catch(() => {});
      })
      .catch(() => setError('This product is no longer available.'))
      .finally(() => setLoading(false));
  }, [token, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030a14] flex items-center justify-center" data-testid="showcase-loading">
        <div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-[#030a14] flex items-center justify-center px-6" data-testid="showcase-error">
        <div className="text-center">
          <Package className="h-16 w-16 text-slate-700 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Product Unavailable</h1>
          <p className="text-sm text-slate-500">{error || 'This product could not be found.'}</p>
        </div>
      </div>
    );
  }

  const cat = CATEGORIES[product.category] || CATEGORIES.other;
  const images = product.image_urls || [];
  const unitLabel = formatUnitLabel(product.unit);
  const shareUrl = window.location.href;
  const companyName = product.company_name || 'Konnect';
  const companyEmail = product.company_email || '';

  return (
    <div ref={pageRef} className="min-h-screen bg-[#030a14] text-white" data-testid="public-showcase-page">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-emerald-500/[0.03] blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-blue-500/[0.02] blur-[120px]" />
      </div>

      <div className="relative z-10">
        {/* Sticky Header */}
        <header className="sticky top-0 z-30 bg-[#030a14]/80 backdrop-blur-xl border-b border-white/[0.04]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="h-3.5 w-3.5 text-emerald-400" />
              </div>
              <span className="text-sm font-semibold text-white/80 truncate" data-testid="showcase-company-name">{companyName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-white/60 hover:text-white hover:bg-white/10 gap-1.5 text-xs h-8"
                onClick={() => setShowShare(true)}
                data-testid="showcase-share-btn"
              >
                <Share2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 text-xs h-8"
                onClick={() => setShowContact(true)}
                data-testid="showcase-contact-btn"
              >
                <Phone className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Contact</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-5xl mx-auto sm:px-6 py-6 sm:py-10">
          {/* Product Header — Mobile: stacked, Desktop: side by side */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
            {/* Media Section — full bleed on mobile */}
            <div className="flex-1 min-w-0">
              <ShowcaseCarousel images={images} videoUrl={product.video_url} />
            </div>

            {/* Product Details */}
            <div className="w-full lg:w-[340px] xl:w-[380px] flex-shrink-0 space-y-5 px-4 sm:px-0">
              {/* Category */}
              <Badge className={cn("text-[10px] border", cat.color)} data-testid="showcase-category">
                {cat.label}
              </Badge>

              {/* Name */}
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight" data-testid="showcase-product-name">
                {product.name}
              </h1>

              {/* Price Block */}
              <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-br from-emerald-500/10 via-emerald-600/5 to-transparent border border-emerald-500/15" data-testid="showcase-price-block">
                <p className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{formatCurrency(product.price, product.currency)}</p>
                {unitLabel && <p className="text-xs text-emerald-400/70 mt-1">{unitLabel}</p>}
              </div>

              {/* Description */}
              {product.description && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 font-medium">About</p>
                  <p className="text-sm text-slate-300 leading-relaxed" data-testid="showcase-description">{product.description}</p>
                </div>
              )}

              {/* Tags */}
              {(product.tags || []).length > 0 && (
                <div className="flex gap-1.5 flex-wrap" data-testid="showcase-tags">
                  {product.tags.map(t => (
                    <span key={t} className="text-[10px] px-2.5 py-1 rounded-full bg-white/[0.04] text-slate-400 border border-white/[0.06]">
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {/* SKU */}
              {product.sku && (
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>SKU</span>
                  <span className="font-mono text-slate-400" data-testid="showcase-sku">{product.sku}</span>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="space-y-2.5 pt-2">
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white gap-2 h-11 text-sm font-medium"
                  onClick={() => setShowContact(true)}
                  data-testid="showcase-cta-contact"
                >
                  <Phone className="h-4 w-4" />
                  Contact Us
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white/10 text-white/70 hover:text-white hover:bg-white/5 gap-2 h-10 text-sm"
                  onClick={() => setShowShare(true)}
                  data-testid="showcase-cta-share"
                >
                  <Share2 className="h-4 w-4" />
                  Share This Product
                </Button>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/[0.04] mt-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-emerald-500/20 flex items-center justify-center">
                <span className="text-emerald-400 text-[10px] font-bold">K</span>
              </div>
              <span className="text-[10px] text-slate-600">Powered by Konnect</span>
            </div>
            <span className="text-[10px] text-slate-700">{companyName}</span>
          </div>
        </footer>
      </div>

      {/* Mobile-optimized Share Drawer */}
      <ShareDrawer
        open={showShare}
        onClose={() => setShowShare(false)}
        url={shareUrl}
        productName={product.name}
        price={formatCurrency(product.price, product.currency)}
        imageUrl={(product.image_urls || [])[0] || ''}
        shareToken={product.share_token || token}
      />

      {/* Contact Drawer */}
      <ContactDrawer
        open={showContact}
        onClose={() => setShowContact(false)}
        product={product}
        companyName={companyName}
        companyEmail={companyEmail}
        companyPhone={product.company_phone || ''}
        channels={product.contact_channels || {}}
        voiceSlug={product.voice_slug || null}
      />

      {/* CSS Animation */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
